import { InformationRequestStatus } from "@tfg/db/browser"
import { createContext, useEffect, type ReactNode } from "react"

import { LibraryResponses } from "@tfg/types/endpoints/library"
import { updateOperationsValidationType } from "@tfg/types/schemas/modules/traceability/queries/find-batch-update-operations.request.dto"

import { CustomFieldValueWithDefinitionAndDocumentResponseDto } from "~/gen/api"
import { OperationNotCreated } from "~/pages/SupplierPortal/TraceabilityRequests/components/OperationNotCreated"
import { MaskedOperationActivity } from "~/pages/SupplierPortal/TraceabilityRequests/components/MaskedOperationActivity"
import { useInformationRequestContext } from "~/pages/SupplierPortal/TraceabilityRequests/store/InformationRequestContext"
import { getOperationItemIsTransferred } from "~/pages/SupplierPortal/TraceabilityRequests/utils/traceability.utils"

import { useUpdateOperationsMutation } from "../hooks"
import {
  OperationItem,
  allChildrenItems,
  nodeRequiresRef,
  calculateOperationProgress,
  RequestWithOpItemFromOperationTree,
} from "../utils"

export type TraceabilityOperationsContextType = {
  request: RequestWithOpItemFromOperationTree
  status: InformationRequestStatus
  operationItems: OperationItem[]
  updateOperationAndChildren: (
    update: updateOperationsValidationType["update"]
  ) => void
  isTranfering: boolean
  isReinforcedTraceability: boolean
  operationItem: OperationItem
  isTransferred: boolean
  currentContactId: string
  user: LibraryResponses.TFGUser
  isAdmin: boolean
  mandatoryReference: boolean
  isParentDisplayed: boolean
}

export const TraceabilityOperationsContext =
  createContext<TraceabilityOperationsContextType | null>(null)

export function TraceabilityOperationsProvider({
  children,
  request,
  status,
  operationItems,
  operationItem,
  isParentDisplayed,
  setProgress,
}: {
  children: ReactNode
  request: RequestWithOpItemFromOperationTree
  status: InformationRequestStatus
  operationItems: OperationItem[]
  operationItem: OperationItem
  isParentDisplayed: boolean
  setProgress: (operationId: number, value: number) => void
}) {
  const { user, isAdmin, customFieldValues, progressByOperationId } =
    useInformationRequestContext()
  const isMasked = operationItem.isMasked === true
  const isReinforcedTraceability =
    request.original.reinforcedTraceability && operationItem.rank >= 2
  const currentContactId = request.original.contactId
  const isTransferred = isMasked
    ? true
    : getOperationItemIsTransferred(
        operationItem,
        operationItem.operation?.owner.id,
        isReinforcedTraceability,
        user?.id
      )

  useEffect(() => {
    if (isMasked) {
      return
    }

    const operationId = operationItem.operation?.id
    if (operationId === undefined) {
      return
    }

    const nextProgress = calculateOperationProgress(
      isTransferred,
      operationItem,
      operationItems,
      customFieldValues as unknown as CustomFieldValueWithDefinitionAndDocumentResponseDto[]
    )

    if (progressByOperationId[operationId] === nextProgress) {
      return
    }

    setProgress(operationId, nextProgress)
  }, [
    isMasked,
    operationItem,
    customFieldValues,
    isTransferred,
    operationItems,
    progressByOperationId,
    setProgress,
  ])

  const mandatoryReference = isMasked
    ? false
    : nodeRequiresRef(
        operationItem.parentOperation ?? null,
        operationItem.operation ?? null
      )

  const { mutate: updateOperationsMutation, isPending: isTranfering } =
    useUpdateOperationsMutation(request.original.id)

  const updateOperationAndChildren = (
    update: updateOperationsValidationType["update"]
  ) => {
    if (isMasked) {
      return
    }

    if (shouldSkipUpdate(update.contactId, currentContactId)) {
      return
    }

    const eligibleOperations = getEligibleOperations(
      operationItem,
      operationItems
    )
    const operationIds = filterOperationIds(
      eligibleOperations,
      update,
      isReinforcedTraceability
    )

    if (operationIds.length > 0) {
      updateOperationsMutation({ ids: operationIds, update })
    }
  }

  if (!user?.id) {
    return null
  }

  if (isMasked) {
    return (
      <MaskedOperationActivity
        operationItem={operationItem}
        request={request}
      />
    )
  }

  if (!operationItem.operation) {
    return <OperationNotCreated operationItem={operationItem} />
  }

  return (
    <TraceabilityOperationsContext.Provider
      value={{
        request,
        status,
        operationItems,
        updateOperationAndChildren,
        isTranfering,
        isReinforcedTraceability,
        operationItem,
        isTransferred,
        currentContactId,
        user,
        isAdmin,
        mandatoryReference,
        isParentDisplayed,
      }}
    >
      {children}
    </TraceabilityOperationsContext.Provider>
  )
}

const shouldSkipUpdate = (newContactId?: string, currentContactId?: string) => {
  return newContactId === currentContactId
}

const getEligibleOperations = (
  currentOperation: OperationItem,
  allOperations: OperationItem[]
): OperationItem[] => {
  return [
    currentOperation,
    ...allChildrenItems(allOperations, currentOperation.nodeId),
  ]
}

const filterOperationIds = (
  operations: OperationItem[],
  update: updateOperationsValidationType["update"],
  isReinforcedTraceability: boolean
): number[] => {
  if (operations.length === 0) {
    return []
  }
  return operations.flatMap((operation: OperationItem): number[] => {
    if (
      !operation.operation ||
      operation.operation.productionSiteId ||
      !isValidOperation(operation)
    ) {
      return []
    }

    if (isReinforcedTraceability) {
      const id = handleReinforcedTraceability(operation, update)
      if (id) {
        return [id]
      }
    }

    return [operation.operation.id]
  })
}

const isValidOperation = (operation: OperationItem): boolean => {
  return !!(operation.operation && !operation.operation.productionSiteId)
}

const handleReinforcedTraceability = (
  operation: OperationItem,
  update: updateOperationsValidationType["update"]
): number | null => {
  if (!operation.operation || update.doNotKnow !== undefined) {
    return null
  }
  return operation.operation.id
}
